import { Router, activationStrategy } from 'aurelia-router';
import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ValidateResult } from 'aurelia-validation';
import { BaseView } from 'Common/BaseView';
import { Config } from 'Config';
import { DataService } from 'DataAccessLayer/Services/DataService';
import { I18N } from 'aurelia-i18n';
import { Layout } from 'Common/Layout/Layout';
import { LayoutHelper } from 'Common/Layout/LayoutHelper';
import { ObjectHelper } from 'Code/Helpers/ObjectHelper';
import { PromptController } from 'Code/Prompts/PromptController';
import { SaveProgress } from 'Code/Extension/Progress';
import { StringHelper } from 'Code/Helpers/StringHelper';
import { SystemService } from 'DataAccessLayer/Services/SystemService';
import { Toastr } from 'Code/Extension/Toastr';
import { ValidationHelper } from 'Code/Helpers/ValidationHelper';
import { FlagStatusHelper } from 'Code/Helpers/FlagStatusHelper';
import { ObjectCompareResult } from '../../../../Code/Helpers/ObjectHelper';

@autoinject
export class CreateBaseView extends BaseView {

    @bindable({ defaultBindingMode: bindingMode.twoWay }) layout: Layout = null;
    @bindable({ defaultBindingMode: bindingMode.toView }) entityName: string = null;
    @bindable({ defaultBindingMode: bindingMode.toView }) sourceEntityName: string = null;
    @bindable({ defaultBindingMode: bindingMode.toView }) sourceEntityId: string = null;
    @bindable({ defaultBindingMode: bindingMode.twoWay }) extData: Object = null;
    @bindable({ defaultBindingMode: bindingMode.toView }) actionName: string = null;
    @bindable({ defaultBindingMode: bindingMode.toView }) actionTypeName: string = 'CreateFrom';
    @bindable({ defaultBindingMode: bindingMode.toView }) methodMode: string = 'QueryBuilder';
    @bindable({ defaultBindingMode: bindingMode.toView }) resultType: string = 'EntityJson';
    @bindable({ defaultBindingMode: bindingMode.twoWay }) entityData: Object = null;
    @bindable({ defaultBindingMode: bindingMode.twoWay }) mode: string = 'create';
    @bindable({ defaultBindingMode: bindingMode.twoWay }) selectedTab: string = 'form';
    @bindable({ defaultBindingMode: bindingMode.twoWay }) documentsCount: number = 0;
    @bindable({ defaultBindingMode: bindingMode.twoWay }) formDoSave: boolean = false;

    protected dataService: DataService = null;
    protected promptController: PromptController = null;
    protected i18n: I18N = null;
    protected router: Router = null;
    protected schema: any = null;
    protected accessRight: Object = null;
    protected validationHelper: ValidationHelper = null;
    protected validationRules: Array<any> = [];
    protected validationRulesItemGrids: any = {};
    protected routeConfig: any = null;
    protected originData: Object = null;
    protected originDataIgnore: Array<string> = null;

    constructor(dataService: DataService, router: Router, validationHelper: ValidationHelper, promptController: PromptController, i18n: I18N) {
        super();
        let tab: string = Config.FetchTabNameFromUrl();
        if (StringHelper.IsNotNullAndEmptyAndUndefined(tab)) {
            this.selectedTab = tab;
        }

        this.dataService = dataService;
        this.router = router;
        this.validationHelper = validationHelper;
        this.promptController = promptController;
        this.i18n = i18n;

        this.testBeforeUnload = e => {
            if (!ObjectHelper.IsNullOrUndefined(this.entityData) && !ObjectHelper.IsNullOrUndefined(this.originData)) {
                let compareResult: ObjectCompareResult = ObjectHelper.Compare(this.entityData, this.originData, this.originDataIgnore, true)
                console.log('testBeforeUnload', compareResult);
                if (!compareResult.Same) {
                    e.preventDefault(); // Cancel the event
                    e.returnValue = '';// Chrome requires returnValue to be set
                }
            }
        };
    }

    protected testBeforeUnload(e) {
    }

    attached() {
        window.addEventListener('beforeunload', this.testBeforeUnload);
    }

    detached() {
        window.removeEventListener('beforeunload', this.testBeforeUnload);
    }

    async activate(params, routeConfig, $navigationInstruction) {
        this.entityName = params.entityName;
        this.routeConfig = routeConfig;

        if (params.hasOwnProperty('sourceEntityId') && params.hasOwnProperty('sourceEntityName')) {
            this.sourceEntityId = params.sourceEntityId;
            this.sourceEntityName = params.sourceEntityName;
        }

        if (params.hasOwnProperty('extData')) {
            let parseSuccess = false;
            try {
                this.extData = JSON.parse(window.atob(params.extData)) || null;
                parseSuccess = true;
            } catch (e) {
                this.extData = null;
            }
            if (!parseSuccess) {
                try {
                    this.extData = JSON.parse(decodeURIComponent(window.atob(params.extData))) || null;
                } catch (e) {
                    this.extData = null;
                }
            }
        }
        if (params.hasOwnProperty('actionName')) {
            this.actionName = params.actionName;
        }
        if (params.hasOwnProperty('actionTypeName')) {
            this.actionTypeName = params.actionTypeName;
        }
        if (params.hasOwnProperty('methodMode')) {
            this.methodMode = params.methodMode;
        }
        if (params.hasOwnProperty('resultType')) {
            this.resultType = params.resultType;
        }
    }

    async bind() {
        this.schema = await SystemService.Instance.GetSchemaByEntity(this.entityName);
        this.layout = await SystemService.Instance.GetEditLayoutForEntity(this.entityName);
        this.originDataIgnore = await SystemService.Instance.GetColumnListToIgnore(this.entityName);

        if (ObjectHelper.IsNullOrUndefined(this.layout)) {

            //
            this.layout = await LayoutHelper.CreateDefaultLayout(this.schema);

            //
            await SystemService.Instance.SaveLayoutForEntity(this.entityName, this.mode, this.layout, null);
        }

        this.accessRight = await SystemService.Instance.GetAccessRightByEntity(this.entityName);
        this.routeConfig.navModel.setTitle((this.schema.hasOwnProperty('EntityCreateTitle') ? this.schema['EntityCreateTitle'] : 'Create') + ' | Bird Review');
        this.validationRules = await this.validationHelper.createRules(this.entityName, this.schema, this.layout, false);

        this.validationRulesItemGrids = {};
        if (ObjectHelper.ArrayIs(this.schema.ItemGrid)) {

            for (let i1 = 0; i1 < this.schema.ItemGrid.length; i1++) {
                const entityName1: string = this.schema.ItemGrid[i1].EntityName;
                let schema1 = await SystemService.Instance.GetSchemaByEntity(entityName1);
                this.validationRulesItemGrids[entityName1] = await this.validationHelper.createRules(entityName1, schema1, null, true, true);
            }
        }
    }

    entityDataChanged(newValue, oldValue) {
        if (ObjectHelper.IsNullOrUndefined(this.originData)) {
            this.originData = JSON.parse(JSON.stringify(this.entityData));
        }
    }

    formDoSaveChanged(newValue, oldValue) {
        if (newValue) {
            this.SaveAndRedirect();
            this.formDoSave = false;
        }
    }

    async canDeactivate() {
        if (!ObjectHelper.IsNullOrUndefined(this.entityData) && !ObjectHelper.IsNullOrUndefined(this.originData)) {
            let compareResult: ObjectCompareResult = ObjectHelper.Compare(this.entityData, this.originData, this.originDataIgnore, true)
            if (!compareResult.Same) {
                let promptText = this.i18n.tr('FormWasChanged');
                if (!ObjectHelper.IsNullOrUndefined(compareResult.DifferentField)) {
                    if (compareResult.DifferentField === 'Document') {
                        promptText = promptText + ' - ' + this.i18n.tr('TrackChanges.Fields.AddDocument') + ' / ' + this.i18n.tr('TrackChanges.Fields.RemoveDocument');
                    }
                    else {
                        promptText = promptText + ' - ' + compareResult.DifferentField;
                    }
                }
                let result = await this.promptController.CustomCancel(promptText, null, false, JSON.parse(JSON.stringify([
                    { "state": "save", "title": this.i18n.tr('Prompt.SaveButton'), "type": "primary" },
                    { "state": "not_save", "title": this.i18n.tr('Prompt.NotSaveButton'), "type": "danger" }
                ])));

                if (result == 'save') {
                    await this.Save();
                    return true;
                }

                return (result == 'not_save');
            }

            return true;
        }
        return true;
    }

    public async onChangeEvent() {
        await this.validationHelper.Validate(this.entityName, this.entityData, this.validationRules, this.validationRulesItemGrids);
    }

    public async Save(): Promise<number> {
        const validateResult: Array<ValidateResult> = await this.validationHelper.Validate(this.entityName, this.entityData, this.validationRules, this.validationRulesItemGrids);
        if (ObjectHelper.ArrayIs(validateResult)) {
            Toastr.ValidateError(validateResult);
            return null;
        }
        else {
            SaveProgress.Show();

            let responseSave: any = await this.dataService.Create(this.entityName, this.entityData);
            switch (responseSave['status']) {
                case 'OK': {
                    let mess = await SystemService.Instance.GetStringFromErrorList(responseSave.validationErrList, this.schema);
                    if (StringHelper.StringIs(mess)) {
                        Toastr.Warning('Toastr.Oooops', mess, false);
                    }

                    let passData = {
                        'EntityName': this.entityName,
                        'RecordId': responseSave['data']['EntityID'],
                        'View': 'Create',
                        'ApiMethod': 'Create',
                        'Status': 'OK',
                        'Message': null
                    };
                    window.parent.postMessage(JSON.stringify(passData), "*");

                    // set data and origin data as null 'cause those have to be same after save
                    this.originData = null;
                    this.entityData = null;

                    Toastr.Success('Toastr.Done', 'Toastr.SavingFormWasSuccessful');
                    SaveProgress.Hide();
                    return responseSave['data']['EntityID'];

                }
                case 'InvalidJson': {
                    let messValidator = '';
                    let messWarning = '';
                    let messRequired = '';
                    if (ObjectHelper.ArrayIs(responseSave.data)) {
                        for (let i1 = 0; i1 < responseSave.data.length; i1++) {
                            const item1: any = responseSave.data[i1];
                            if (item1.ErrorType.toLowerCase() == 'validator') {
                                messValidator = messValidator + await SystemService.Instance.GetPathLocalizationString(item1, this.schema) + " - " + item1.Message + '<br>';
                            }
                            if (item1.ErrorType.toLowerCase() == 'warning') {
                                messWarning = messWarning + await SystemService.Instance.GetPathLocalizationString(item1, this.schema) + " - " + item1.Message + '<br>';
                            }
                            if (item1.ErrorType.toLowerCase() == 'required') {
                                messRequired = messRequired + item1.Message + '<br>';
                            }
                        }
                    }
                    if (StringHelper.StringIs(messValidator)) {
                        Toastr.Error('Toastr.ThisFormIsNotValid', messValidator, false);
                    }
                    if (StringHelper.StringIs(messWarning)) {
                        Toastr.Warning('Toastr.Oooops', messWarning, false);
                    }
                    if (StringHelper.StringIs(messRequired)) {
                        Toastr.Error('Toastr.ThisFormIsNotValid', messRequired, false);
                    }
                    break;
                }
                default: {
                    this.ApiCrudShowToastrError(null, this.entityName, responseSave);
                }
            }
            SaveProgress.Hide();
            return null;
        }
    }

    public GoBack() {

        //
        window.history.back();
    }

    public GoToList() {

        //
        this.router.navigateToRoute('entityList', { entityName: this.entityName });
    }

    public async SaveAndRedirect() {
        let entityId = await this.Save();
        if (entityId != null && entityId > 0 && (this.accessRight['Read'] || this.accessRight['Edit'] == true)) {
            this.router.navigateToRoute('entityEdit', { entityName: this.entityName, entityId: entityId }, { replace: true });
        }
        else {
            //this.router.navigateToRoute('entityCreate', { entityName: this.entityName }, { replace: true });
        }
    }

    public async SaveAndGoBack() {
        let entityId = await this.Save();
        if (entityId != null && entityId > 0) {
            this.GoBack();
        }
    }

    public async SaveAndGoToList() {
        let entityId = await this.Save();
        if (entityId != null && entityId > 0) {
            this.GoToList();
        }
    }

    public async SendEmail(): Promise<void> {
        this.entityData['FlagStatus'] = FlagStatusHelper.SetIsDraft(this.entityData['FlagStatus'], false);
        this.entityData['SendByService'] = true;
        let entityId = await this.Save();
        if (entityId != null && entityId > 0) {
            this.router.navigateToRoute('entityEdit', { entityName: this.entityName, entityId: entityId }, { replace: true });
        }
    }
    public get canSend() {
        if (ObjectHelper.ObjectIs(this.entityData) && ObjectHelper.ObjectIs(this.entityData['FlagStatus'])) {
            return FlagStatusHelper.IsDraft(this.entityData['FlagStatus']);
        }
        return false;
    }

    public async CarRentalTakeoverProtocolCreate(): Promise<void> {
        this.entityData['ProgressBar_FK'] = 15196;
        let entityId = await this.Save();
        if (entityId != null && entityId > 0) {
            this.router.navigateToRoute('entityEdit', { entityName: this.entityName, entityId: entityId }, { replace: true });
        }
    }
}
